import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Input } from "@/components/ui/inputs/input";
import ToastMe from "@/components/ui/ToastMe";
import SystemService from "@/services/SystemService";

const BRAND = {
  background: "#f4f6fb",
  card: "#ffffff",
  primary: "#156372",
  accent: "#FFC107",
};

const ActivatePage = () => {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      licenseKey: "",
      mongoUri: "",
      licenseServerUrl: "",
    },
  });

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [activating, setActivating] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const data = await SystemService.fetchLicenseStatus();
      setStatus(data);
      if (data?.serverUrl) {
        setValue("licenseServerUrl", data.serverUrl);
      }
    } catch (error) {
      ToastMe(
        error?.response?.data?.message || error?.message || "Unable to load activation status.",
        "error",
      );
    } finally {
      setLoadingStatus(false);
    }
  }, [setValue]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const isAlreadyActive = useMemo(
    () => !!status?.cacheValid && status?.enforce && !status?.locked,
    [status],
  );

  const onSubmit = handleSubmit(async (values) => {
    setActivating(true);
    try {
      const response = await SystemService.activate(values);
      const toastType = response?.status === "ok" ? "success" : "warning";
      const extraMessages = [];

      if (response?.seed?.ran) {
        const exitCode = response.seed.exitCode ?? 0;
        extraMessages.push(
          exitCode === 0
            ? "Database seed completed."
            : "Database seed exited with code " + exitCode + ".",
        );
      } else if (response?.seed?.reason === "disabled") {
        extraMessages.push("Auto-seed is disabled.");
      } else if (response?.seed?.reason === "in_progress") {
        extraMessages.push("Database seed is already running.");
      } else if (response?.seed?.reason === "mongo_unavailable") {
        extraMessages.push("Database seed skipped because MongoDB was unavailable.");
      } else if (response?.seed?.error) {
        extraMessages.push("Seed error: " + response.seed.error);
      }

      const toastMessage = [response?.message || "Activation completed.", ...extraMessages].join(" ");
      ToastMe(toastMessage.trim(), toastType);
      await fetchStatus();
      if (response?.status === "ok") {
        navigate("/setup", { replace: true });
      }
    } catch (error) {
      ToastMe(
        error?.response?.data?.message || error?.message || "Activation failed. Please retry.",
        "error",
      );
    } finally {
      setActivating(false);
    }
  });

  return (
    <section
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: BRAND.background }}
    >
      <div className="w-full max-w-4xl">
        <div className="rounded-3xl shadow-xl border border-slate-200 overflow-hidden bg-white">
          <div
            className="px-6 py-8 md:px-10 md:py-12 text-center text-white"
            style={{
              background:
                "linear-gradient(135deg, " + BRAND.primary + " 0%, " + BRAND.accent + " 100%)",
            }}
          >
            <h1 className="text-3xl md:text-4xl font-semibold text-white text-center mb-3">
              Activate Your Installation
            </h1>
            <p className="text-white/90 text-center max-w-2xl mx-auto">
              Enter the license key you received from the SaaS portal and the MongoDB connection
              string for this deployment. We will verify the license with the licensing service and
              store the configuration securely.
            </p>
          </div>

          <div className="px-6 py-8 md:px-10 md:py-12">
            <StatusSummary status={status} loading={loadingStatus} />

            <form className="space-y-6 mt-8" onSubmit={onSubmit}>
              <Controller
                control={control}
                name="licenseServerUrl"
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="url"
                    labelText="License Server URL"
                    placeholder="https://license.yourdomain.com"
                    errorType={errors?.licenseServerUrl ? "required" : undefined}
                    customMessage="License server URL is required."
                  />
                )}
              />

              <Controller
                control={control}
                name="licenseKey"
                rules={{ required: true, minLength: 10 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    autoCapitalize="characters"
                    autoComplete="off"
                    labelText="License Key"
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    errorType={errors?.licenseKey?.type}
                    customMessage="License key is required."
                  />
                )}
              />

              <Controller
                control={control}
                name="mongoUri"
                rules={{ required: true, minLength: 12 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    autoComplete="off"
                    labelText="MongoDB Connection URI"
                    placeholder="mongodb+srv://user:password@cluster.mongodb.net/app"
                    errorType={errors?.mongoUri?.type}
                    customMessage="MongoDB connection string is required."
                  />
                )}
              />

              <button
                type="submit"
                disabled={activating}
                className="w-full md:w-auto font-semibold rounded-xl px-8 py-3 transition disabled:opacity-70"
                style={{
                  backgroundColor: BRAND.primary,
                  color: "#ffffff",
                  boxShadow: "0 15px 35px rgba(21, 99, 114, 0.25)",
                }}
              >
                {activating ? "Activating…" : "Validate & Activate"}
              </button>
            </form>

            {isAlreadyActive && (
              <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-700">
                <h3 className="font-semibold text-emerald-800">This instance is already active.</h3>
                <p className="text-sm mt-2">
                  You can re-run activation if you need to update the database connection or license
                  key. Restart the backend server after changes to ensure integrations reconnect
                  cleanly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const StatusSummary = ({ status, loading }) => {
  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-slate-600">
        Checking activation status…
      </div>
    );
  }

  if (!status) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700">
        Unable to load activation status. Please verify the backend is reachable.
      </div>
    );
  }

  const { status: code, activation, mongoConnected, locked } = status;
  const badgeClass =
    code === "ok" && !locked
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : "bg-amber-100 text-amber-700 border-amber-200";

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-2xl border px-4 py-4 bg-white shadow-sm">
        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${badgeClass}`}>
          {locked ? "Activation Required" : code === "ok" ? "Active" : "Pending"}
        </span>
        <h3 className="mt-3 text-lg font-semibold text-slate-800">License Status</h3>
        <ul className="mt-3 space-y-1 text-sm text-slate-600">
          <li>
            <strong>Tenant:</strong> {activation?.tenantId || <span className="text-slate-400">n/a</span>}
          </li>
          <li>
            <strong>Activation ID:</strong> {activation?.activationId || <span className="text-slate-400">n/a</span>}
          </li>
          <li>
            <strong>Expires:</strong> {activation?.expiresAt ? new Date(activation.expiresAt).toLocaleString() : "No expiry"}
          </li>
          <li>
            <strong>Last Verified:</strong> {activation?.lastVerifiedAt ? new Date(activation.lastVerifiedAt).toLocaleString() : "Not yet verified"}
          </li>
        </ul>
      </div>

      <div className="rounded-2xl border px-4 py-4 bg-white shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800">Environment</h3>
        <ul className="mt-3 space-y-1 text-sm text-slate-600">
          <li>
            <strong>License enforcement:</strong> {status?.enforce ? "Enabled" : "Disabled"}
          </li>
          <li>
            <strong>Auto-run seed:</strong> {status?.runSeedOnActivation ? "Enabled" : "Disabled"}
          </li>
          <li>
            <strong>MongoDB connected:</strong> {mongoConnected ? "Yes" : "No"}
          </li>
          <li>
            <strong>License server:</strong> {status?.serverUrl || "Not configured"}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ActivatePage;
